import React, { useState, useEffect } from 'react';
import useBreadcrumbs from '../../utils/hooks/useBreadcrumbs';
import TitlePage from '../../UI/TitlePage/TitlePage';
import GuestbookStatistics from './components/GuestbookStatistics';
import GuestbookEntryList from './components/GuestbookEntryList';
import GuestbookAllEntries from './components/GuestbookAllEntries';
import { GuestbookStatistics as GuestbookStatisticsType } from '../../Types/guestbook';
import GuestbookAPI from '../../services/api/GuestbookService';

const GuestbookManagement: React.FC = () => {
  const [statistics, setStatistics] = useState<GuestbookStatisticsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'all'>('pending');

  useBreadcrumbs([
    { label: 'Tableau de bord', is_link: true, href: '/dashboard' },
    { label: 'Livre d\'or', is_link: false }
  ]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await GuestbookAPI.get_statistics();
      if (response && (response as any).data) {
        setStatistics((response as any).data);
        setError(null);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleEntryAction = () => {
    // Rafraîchir les statistiques après une action sur un témoignage
    fetchStatistics();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Erreur
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TitlePage>
        Gestion du Livre d'Or
      </TitlePage>
      
      {statistics && (
        <GuestbookStatistics 
          statistics={statistics} 
          onRefresh={fetchStatistics}
        />
      )}
      
      {/* Onglets */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('pending')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'pending'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Témoignages en attente</span>
                {statistics && statistics.pending_entries > 0 && (
                  <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {statistics.pending_entries}
                  </span>
                )}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'all'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Tous les témoignages</span>
                {statistics && (
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    {statistics.total_entries}
                  </span>
                )}
              </div>
            </button>
          </nav>
        </div>
        
        <div className="p-6">
          {activeTab === 'pending' ? (
            <GuestbookEntryList onEntryAction={handleEntryAction} />
          ) : (
            <GuestbookAllEntries onEntryAction={handleEntryAction} />
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestbookManagement; 